export type QuestionFilters = { moduleId: string, sessionId: string, groupId: string, from: string, to: string }

export const NO_FILTERS: QuestionFilters = { moduleId: '', sessionId: '', groupId: '', from: '', to: '' }

export type SessionOption = { id: string, name: string, modules?: { module?: { id: string, title: string } | null }[] | null }

/** Les modules proposés : ceux des parcours, ou du seul parcours choisi, par titre. */
export function moduleOptions(sessions: SessionOption[], sessionId: string): { id: string, title: string }[] {
  const inScope = sessionId ? sessions.filter(session => session.id === sessionId) : sessions
  const modules = new Map<string, { id: string, title: string }>()
  for (const session of inScope) {
    for (const step of session.modules ?? []) {
      if (step.module) modules.set(step.module.id, step.module)
    }
  }
  return [...modules.values()].sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * Les filtres amorcés par l'URL (`?sessionId=…&moduleId=…`), comme le lien de
 * la fiche session (#243). Lus une seule fois, à l'ouverture : les sélecteurs
 * reprennent la main ensuite.
 */
export function filtersFromUrl(params: URLSearchParams): QuestionFilters {
  return { ...NO_FILTERS, sessionId: params.get('sessionId') ?? '', moduleId: params.get('moduleId') ?? '' }
}

/**
 * Les filtres effectivement appliqués : un parcours inconnu (supprimé, lien
 * collé d'ailleurs) retombe sur « tous », un module absent du parcours sur
 * « aucun » — plutôt qu'un filtre actif dont le nom n'apparaît nulle part.
 * `sessions` vaut null tant que la liste n'est pas arrivée : rien n'est retiré
 * d'ici là. Arrivée vide ou en échec, elle ne connaît aucun parcours.
 */
export function knownFilters(filters: QuestionFilters, sessions: SessionOption[] | null): QuestionFilters {
  if (sessions === null) return filters
  const sessionId = sessions.some(session => session.id === filters.sessionId) ? filters.sessionId : ''
  const moduleId = moduleOptions(sessions, sessionId).some(module => module.id === filters.moduleId) ? filters.moduleId : ''
  return { ...filters, sessionId, moduleId }
}

/** Change un filtre ; un module absent du parcours choisi ne reste pas sélectionné. */
export function withFilter<K extends keyof QuestionFilters>(
  current: QuestionFilters,
  key: K,
  value: QuestionFilters[K],
  sessions: SessionOption[],
): QuestionFilters {
  const next = { ...current, [key]: value }
  if (key === 'sessionId' && value && !moduleOptions(sessions, value).some(module => module.id === current.moduleId)) {
    next.moduleId = ''
  }
  return next
}

/** La période est-elle à l'envers ? L'analyse n'est alors pas lancée : « aucune réponse » tromperait. */
export function isPeriodReversed({ from, to }: QuestionFilters): boolean {
  return Boolean(from && to && from > to)
}

/** Ce que l'API reçoit des filtres : les seuls renseignés. */
export function questionsInput(filters: QuestionFilters): { moduleId: string, sessionId?: string, groupId?: string, from?: string, to?: string } {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value)) as { moduleId: string }
}
